import { FC } from 'react';

import { router } from '@inertiajs/react';
import { clsx } from 'clsx';
import {
    Check,
    ChevronDown,
    LayoutGrid,
    List,
    Pause,
    Search,
    Trash,
} from 'lucide-react';

import { QuickStatusFilters } from './QuickStatusFilters';
import { Button } from '../../../../components/ui/Button';
import { Checkbox } from '../../../../components/ui/Checkbox';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../../../components/ui/Dropdown-Menu';
import { Input } from '../../../../components/ui/Input';
import { Separator } from '../../../../components/ui/Separator';
import { Routes } from '../../../../models/routes';
import { LocalStorageService } from '../../../../services/LocalStorageService';
import {
    Offer,
    OfferActionQuantity,
    OfferActionTypes,
    OfferType,
} from '../../../../types';
import { LocalStorageKeys } from '../../../../types/localStorage';
import { ConfirmModalState, ViewMode } from '../../../../types/ui';

interface Props {
    offers: Offer[];
    search: string;
    status: OfferType;
    sort: string;
    filters: Record<string, string>;
    expansionSlug?: string;
    setSearch: (value: string) => void;
    setStatus: (status: OfferType) => void;
    disabled: boolean;
    setLoading: (loading: boolean) => void;
    viewMode: ViewMode;
    setViewMode: (mode: ViewMode) => void;
    selectedOffers: number[];
    setSelectedOffers: (offers: number[]) => void;
    offersCounts: Record<OfferType, number>;
    setConfirmModalState: (state: ConfirmModalState) => void;
}

export const ActionsRow: FC<Props> = ({
    offers,
    search,
    status,
    sort,
    filters,
    expansionSlug,
    setSearch,
    setStatus,
    disabled,
    setLoading,
    viewMode,
    setViewMode,
    selectedOffers,
    setSelectedOffers,
    offersCounts,
    setConfirmModalState,
}) => {
    const selectAllOffers = () => {
        if (selectedOffers.length === offers.length) {
            setSelectedOffers([]);
        } else {
            setSelectedOffers(offers.map((offer) => offer.id));
        }
    };

    const handleBulkOption = (type: OfferActionTypes) => {
        setConfirmModalState({
            open: true,
            payload: {
                type,
                quantity: OfferActionQuantity.Bulk,
                offersId: selectedOffers,
            },
        });
    };

    const handleViewModeChange = (viewMode: ViewMode) => {
        setViewMode(viewMode);

        LocalStorageService.setItem(LocalStorageKeys.VIEW_MODE, viewMode);
    };

    const navigate = ({
        search,
        status,
        sort,
    }: {
        search: string;
        status: OfferType;
        sort: string;
    }) => {
        router.get(
            Routes.Offers,
            {
                page: 1,
                append: false,
                sort,
                q: {
                    ...filters,
                    ...(expansionSlug ? { expansion_slug: expansionSlug } : {}),
                    ...(search ? { search } : {}),
                    status,
                },
            },
            {
                preserveScroll: true,
                preserveState: true,
                only: ['initialOffers', 'q', 'nextPage', 'expansions', 'sort'],
                reset: ['initialOffers'],
                onStart: () => {
                    setLoading(true);
                },
                onSuccess: () => {
                    setLoading(false);
                },
            },
        );
    };

    const handleSearch = (search: string, status: OfferType) => {
        navigate({ search, status, sort });
    };

    return (
        <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row md:mb-0">
            <div>
                <div className="flex items-center gap-2">
                    <div
                        onClick={selectAllOffers}
                        className={clsx(
                            'inline-flex h-9 items-center justify-center rounded-xl border border-input bg-background px-3 text-sm font-medium whitespace-nowrap ring-offset-background transition-colors hover:cursor-pointer hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
                            { 'pointer-events-none': disabled },
                        )}
                    >
                        <Checkbox
                            checked={
                                selectedOffers &&
                                selectedOffers.length > 0 &&
                                selectedOffers.length === offers.length
                            }
                            className="mr-2 h-4 w-4"
                        />
                        {selectedOffers.length > 0
                            ? `Обрано (${selectedOffers.length})`
                            : 'Обрати всі'}
                    </div>

                    {selectedOffers.length > 0 && (
                        <DropdownMenu>
                            <DropdownMenuTrigger
                                disabled={disabled}
                                className="flex h-9 w-[140px] cursor-pointer items-center justify-between gap-2 rounded-xl border bg-white px-4 py-2 hover:bg-gray-50 dark:bg-background dark:hover:bg-accent"
                            >
                                Дії
                                <ChevronDown className="h-4 w-4" />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                                <DropdownMenuItem
                                    disabled={status === 'active'}
                                    onClick={() =>
                                        handleBulkOption(
                                            OfferActionTypes.Activate,
                                        )
                                    }
                                >
                                    <div className="flex items-center gap-2">
                                        <Check className="h-3.5 w-3.5" />
                                        Активувати
                                    </div>
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    disabled={status === 'inactive'}
                                    onClick={() =>
                                        handleBulkOption(
                                            OfferActionTypes.Deactivate,
                                        )
                                    }
                                >
                                    <div className="flex items-center gap-2">
                                        <Pause className="h-3.5 w-3.5" />
                                        Деактивувати
                                    </div>
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    className="gap-2"
                                    onClick={() =>
                                        handleBulkOption(
                                            OfferActionTypes.Delete,
                                        )
                                    }
                                >
                                    <Trash className="h-3.5 w-3.5" />
                                    Видалити
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
            </div>

            <QuickStatusFilters
                disabled={disabled}
                onFilterChange={(status) => {
                    setStatus(status);
                    handleSearch(search, status);

                    setSelectedOffers([]);
                }}
                activeFilter={status}
                counts={offersCounts}
            />

            <div
                className={clsx('flex flex-1 justify-between gap-2', {
                    'pointer-events-none': disabled,
                })}
            >
                <div className="relative flex-1">
                    <Search className="absolute top-2.5 left-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                        type="search"
                        placeholder="Пошук пропозицій..."
                        className="h-9 pl-9"
                        value={search}
                        onChange={(event) => {
                            setSearch(event.target.value);

                            handleSearch(event.target.value, status);
                        }}
                    />
                </div>

                <div className="hidden items-center rounded-xl border md:flex">
                    <Button
                        disabled={disabled}
                        variant="ghost"
                        size="icon"
                        className={`h-9 w-9 rounded-none rounded-l-xl ${viewMode === 'grid' ? 'bg-muted' : ''}`}
                        onClick={() => handleViewModeChange('grid')}
                    >
                        <LayoutGrid className="h-4 w-4" />
                    </Button>
                    <Separator orientation="vertical" className="h-5" />
                    <Button
                        disabled={disabled}
                        variant="ghost"
                        size="icon"
                        className={`h-9 w-9 rounded-none rounded-r-xl ${viewMode === 'list' ? 'bg-muted' : ''}`}
                        onClick={() => handleViewModeChange('list')}
                    >
                        <List className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
};
